import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProcessSection from '@/components/feature/ProcessSection';
import WhyChooseUs from '@/components/feature/WhyChooseUs';
import ServiceAreas from '@/components/feature/ServiceAreas';
import Testimonials from '@/components/feature/Testimonials';
import FAQSection from '@/components/feature/FAQSection';
import FinalCTA from '@/components/feature/FinalCTA';
import MidPageCTA from '@/components/feature/MidPageCTA';
import HeroSection from '@/pages/home/components/HeroSection';
import TrustBar from '@/pages/home/components/TrustBar';
import AIAnswerBlock from '@/pages/home/components/AIAnswerBlock';
import ServicesOverview from '@/pages/home/components/ServicesOverview';

const aboutTrustItems = [
  { icon: 'ri-medal-fill', label: '10+ Years in Philadelphia', sub: 'Industry expertise' },
  { icon: 'ri-vip-diamond-line', label: 'Premium Film Brands', sub: 'Professional grade only' },
  { icon: 'ri-window-line', label: '500+ Windows Tinted', sub: 'All makes & properties' },
  { icon: 'ri-star-fill', label: '5.0 Star Rated', sub: 'Google Reviews' },
];

const aboutAIQA = [
  {
    q: 'Who is TINTSPRO X?',
    a: 'TINTSPRO X is Philadelphia\'s professional window tinting studio located at 3001 Ormes St Unit 05. They specialize in automotive, residential, and commercial window tinting — delivering premium film installation for all types of vehicles and properties.',
  },
  {
    q: 'What makes TINTSPRO X different?',
    a: 'TINTSPRO X focuses exclusively on window tinting — not a general auto shop offering tinting as a side service. Every project uses professional-grade film, precision installation techniques, and thorough quality inspection before delivery.',
  },
  {
    q: 'Where is TINTSPRO X located?',
    a: 'TINTSPRO X is located at 3001 Ormes St Unit 05, Philadelphia, PA 19134. They serve all Philadelphia neighborhoods and surrounding areas including South Jersey, Montgomery County, and Bucks County. Call (215) 594-9404 to schedule.',
  },
  {
    q: 'What types of tinting does TINTSPRO X specialize in?',
    a: 'TINTSPRO X specializes in automotive tinting, commercial building tinting, residential home tinting, ceramic tinting, carbon fiber installation, and general window film applications — covering every type of tinting need in the Philadelphia area.',
  },
];

const aboutFAQs = [
  {
    q: 'How long has TINTSPRO X been operating in Philadelphia?',
    a: 'TINTSPRO X has served Philadelphia-area clients for over a decade, building a reputation for quality window tinting across automotive, residential, and commercial applications with consistently professional results.',
  },
  {
    q: 'Are your technicians professionally trained?',
    a: 'Yes. Our tinting technicians are trained in professional film installation for automotive, residential, and commercial glass — ensuring every project receives expert-level care and a flawless, bubble-free finish.',
  },
  {
    q: 'What areas near Philadelphia do you serve?',
    a: 'We serve all of Philadelphia and surrounding areas including Northeast Philly, South Philadelphia, Kensington, Fishtown, Cheltenham Township, Bensalem, and South Jersey communities including Cherry Hill and Pennsauken.',
  },
  {
    q: 'Do you use professional-grade window films?',
    a: 'Absolutely. We source only professional-tier window films with documented performance data on heat rejection, UV blockage, and durability — not consumer-grade or budget materials sold through general retail channels.',
  },
];

const aboutWhyItems = [
  { icon: 'ri-vip-diamond-line', text: 'Professional-Grade Films Only', sub: 'Every film product is sourced at the professional tier — not general retail or budget alternatives.' },
  { icon: 'ri-focus-2-line', text: 'Computer-Cut Precision', sub: 'Templates cut to exact vehicle, window, or glass dimensions for perfect fitting every time.' },
  { icon: 'ri-repeat-line', text: 'Consistent Standards Every Time', sub: 'The same quality applied to every job — whether it\'s a compact car or a commercial building.' },
  { icon: 'ri-shield-check-fill', text: 'Backed by Our Guarantee', sub: 'We stand behind our installations with warranty coverage on materials and workmanship quality.' },
];

const aboutServices = [
  {
    id: 'automotive-tinting', title: 'Automotive Tinting',
    icon: 'ri-car-fill', tag: 'Most Popular',
    desc: 'Professional window tinting for all vehicle types — premium film, heat rejection, UV protection, and privacy.',
    image: 'https://public.readdy.ai/ai/img_res/5367839875178d00c93f0abeb74d540c.jpg',
  },
  {
    id: 'commercial-tinting', title: 'Commercial Tinting',
    icon: 'ri-building-line',
    desc: 'Window film for offices and storefronts — reduce glare, lower energy costs, improve privacy and aesthetics.',
    image: 'https://readdy.ai/api/search-image?query=commercial%20building%20window%20film%20tinting%20professional%20installation%20modern%20office%20glass%20urban%20architecture%20glare%20reduction%20energy%20savings%20Philadelphia%20commercial%20tinting&width=600&height=500&seq=ab-svc-2&orientation=landscape',
  },
  {
    id: 'residential-tinting', title: 'Residential Tinting',
    icon: 'ri-home-4-line',
    desc: 'Home window film to block UV, reduce heat, protect furnishings, and improve privacy without losing natural light.',
    image: 'https://readdy.ai/api/search-image?query=residential%20home%20window%20tinting%20film%20modern%20bright%20interior%20natural%20light%20UV%20protection%20privacy%20home%20window%20film%20application%20Philadelphia%20residential&width=600&height=500&seq=ab-svc-3&orientation=landscape',
  },
  {
    id: 'ceramic-tinting', title: 'Ceramic Tinting',
    icon: 'ri-contrast-drop-2-line',
    desc: 'Top-tier ceramic film for maximum heat and UV rejection — no signal interference, superior clarity and performance.',
    image: 'https://readdy.ai/api/search-image?query=ceramic%20window%20tint%20film%20luxury%20car%20window%20premium%20ceramic%20tinting%20close%20up%20dark%20cinematic%20dramatic%20studio%20high%20contrast%20moody%20automotive%20detail%20professional%20installation%20clear&width=600&height=500&seq=ab-svc-4&orientation=landscape',
  },
];

export default function AboutPage() {
  return (
    <div className="bg-[#080808]">
      <Navbar />
      <HeroSection
        eyebrow="About Us · Philadelphia, PA"
        title="About TINTSPRO X"
        subtitle="Built on precision, premium materials, and a commitment to quality — Philadelphia's trusted professional window tinting studio for automotive, residential, and commercial clients."
        primaryCTA="Work With Our Team"
        imageSrc="https://readdy.ai/api/search-image?query=professional%20window%20tinting%20studio%20interior%20clean%20dark%20automotive%20shop%20luxury%20car%20being%20tinted%20dramatic%20cinematic%20lighting%20moody%20atmosphere%20professional%20shop%20Philadelphia%20high%20contrast%20deep%20shadows%20polished%20environment%20premium%20tint%20studio&width=1920&height=1080&seq=about-hero-tint-001&orientation=landscape"
      />
      <TrustBar items={aboutTrustItems} />

      {/* Company Story */}
      <section className="bg-[#080808] py-28 md:py-36 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 xl:gap-24 items-center">
            <div className="w-full lg:w-[48%] shrink-0">
              <div className="relative">
                <div className="w-full h-[480px] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20window%20tint%20technician%20applying%20film%20luxury%20car%20close%20up%20skilled%20hands%20squeegee%20cinematic%20dark%20studio%20lighting%20dramatic%20moody%20atmosphere%20high%20contrast%20deep%20shadows%20professional%20tinting%20shop%20Philadelphia%20premium%20automotive%20tinting%20service%20precise%20installation&width=900&height=900&seq=about-story-tint-001&orientation=squarish"
                    alt="TINTSPRO X technician at work"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-5 -right-5 bg-[#AAFF00] text-[#080808] rounded-2xl p-5 hidden lg:block">
                  <p className="font-serif text-4xl font-bold leading-none">5.0</p>
                  <div className="flex gap-0.5 my-1">
                    {[1,2,3,4,5].map((s) => (
                      <div key={s} className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-[#080808] text-xs"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-[#080808]/70 text-[11px] font-sans font-medium">Google Rated</p>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-[1px] bg-[#AAFF00]" />
                <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">Our Story</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-6">
                Philadelphia&apos;s Trusted Window Tinting Studio
              </h2>
              <p className="text-white/50 font-sans text-base leading-relaxed mb-5">
                TINTSPRO X was founded to give Philadelphia clients access to a true professional-grade window tinting studio — not a quick-service shop cutting corners with cheap film or rushed installations.
              </p>
              <p className="text-white/50 font-sans text-base leading-relaxed mb-5">
                We specialize exclusively in window tinting — automotive, residential, and commercial — because that focus produces better outcomes. Our technicians are trained specifically in film installation techniques, giving you precision that generalist shops simply can&apos;t match.
              </p>
              <p className="text-white/50 font-sans text-base leading-relaxed mb-8">
                Located at 3001 Ormes St Unit 05 in Philadelphia, we&apos;ve served hundreds of clients across the region with the same uncompromising standard every time. Reach us at (215) 594-9404 or tintspro2@gmail.com.
              </p>

              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/8">
                {[['500+', 'Windows Tinted'], ['5.0★', 'Google Rating'], ['10+', 'Years Active']].map(([val, lbl]) => (
                  <div key={lbl}>
                    <p className="font-serif text-[#AAFF00] text-3xl font-bold leading-none mb-1">{val}</p>
                    <p className="text-white/35 text-xs font-sans">{lbl}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex gap-3 flex-wrap">
                <a href="#estimate" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-7 py-3.5 rounded-full hover:bg-[#C4FF33] transition-colors cursor-pointer">
                  Book Your Tinting
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-sm"></i></div>
                </a>
                <a href="tel:2155949404" className="whitespace-nowrap inline-flex items-center gap-2 border border-white/12 text-white/55 text-sm font-sans px-7 py-3.5 rounded-full hover:border-white/30 hover:text-white transition-all cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-[#AAFF00] text-sm"></i></div>
                  (215) 594-9404
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServicesOverview
        heading="Our Tinting Services"
        subheading="A focused overview of what we offer — every service backed by premium film and expert installation."
        services={aboutServices}
        showCTA
      />
      <MidPageCTA
        heading="Professional Window Tinting in Philadelphia"
        subheading="Free quotes · Same-day automotive tinting available · Professional results."
        ctaLabel="Get Free Quote"
        variant="gold"
      />
      <AIAnswerBlock heading="About TINTSPRO X" items={aboutAIQA} />
      <ProcessSection />
      <WhyChooseUs
        title="Craftsmanship, Consistency,\nand Quality Films."
        subtitle="Every project gets the same professional attention — from a first-time vehicle tint to a full commercial building installation."
        items={aboutWhyItems}
      />
      <MidPageCTA heading="Start Your Tinting Project Today" subheading="No obligation. We respond within 24 hours." ctaLabel="Book Consultation" variant="dark" />
      <ServiceAreas />
      <Testimonials />
      <FAQSection heading="Questions About Our Studio" items={aboutFAQs} />
      <FinalCTA
        heading="Work With Philadelphia&#39;s&#10;Trusted Tint Studio"
        subheading="TINTSPRO X — professional window tinting for automotive, residential, and commercial clients."
        buttonLabel="Get Started Today"
      />
      <Footer />
    </div>
  );
}
